import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { showToast } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { numberFormat } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { SettingsPath } from 'entities/ExportRouters'
import { BillingApi, PriceInfoStore } from 'entities/Billing'
import { getBillingLabel, getIntervalMonthCount } from 'entities/Billing/lib/billingInterval'
import { ModalSecondStepActions } from 'pages/settings/ui/AddOnModal/ui/ModalSecondStepActions/ModalSecondStepActions'
import { ModalFirstStepActions } from 'pages/settings/ui/AddOnModal/ui/ModalFirstStepActions/ModalFirstStepActions'
import { ModalContentFirstStep } from 'pages/settings/ui/AddOnModal/ui/ModalContentFirstStep/ModalContentFirstStep'
import { ContentSecondStep } from 'pages/settings/ui/AddOnModal/ui/ContentSecondStep/ContentSecondStep'

export class AddOnModalStore {
  private _stepsCount: number
  private _regStep = 1
  private _modalId = nanoid()
  private _priceInfoStore: PriceInfoStore

  private _loading = false
  private _paymentError = false

  constructor(
    private _entities: IEntitiesStore,
    stepsCount: number,
    private _preventSubmitModal: () => boolean = () => false
  ) {
    this._stepsCount = stepsCount
    this._priceInfoStore = new PriceInfoStore((count, cancelToken) =>
      BillingApi.getTrustedCallingBilling(
        {
          plan: this._entities.subscriptionStore.plan?.compliance_plan_id || '',
        },
        {
          cancelToken: cancelToken,
        }
      )
    )

    makeAutoObservable(this)
  }

  get regStep() {
    return this._regStep
  }

  get stepsCount() {
    return this._stepsCount
  }

  get isFirstStep() {
    return this.regStep === 1
  }

  get priceInfoStore() {
    return this._priceInfoStore
  }

  get isPaymentError() {
    return this._paymentError
  }

  get loading() {
    return this._loading
  }

  get isActive() {
    return this._entities.billingStore.billingComplianceInfo?.subscribed ?? false
  }

  get trustedCallingPriceText() {
    const interval = this._entities.subscriptionStore.plan?.interval || 'monthly'
    const subtotal = getIntervalMonthCount(interval) * 10

    return `${numberFormat({
      value: subtotal,
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} / ${getBillingLabel(interval)}`
  }

  setRegStep(step: number) {
    this._regStep = step
  }

  firstStepAction = () => {
    this.closeGetAddOnModal()
    this.setRegStep(2)

    this.openGetAddOnModalSecondStep()
  }

  secondStepAction = async () => {
    if (this._preventSubmitModal()) {
      return
    }
    this.setRegStep(1)

    try {
      this._loading = true

      await this._entities.billingStore.updateBillingComplianceInfo()

      this.closeGetAddOnModal()

      showToast({
        type: 'success',
        title: 'Trusted Calling enabled',
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this._paymentError = true
        if (e.errors.getError('message') === 'Your card was declined.') {
          showToast({
            type: 'error',
            title: e.errors.getError('message'),
          })
        }
      }
    } finally {
      this._loading = false
    }
  }

  closeGetAddOnModal = () => {
    this.setRegStep(1)
    modalStore.closeModal(this._modalId)
  }

  returnToFirstStep = async () => {
    this._paymentError = false
    this._loading = false
    this.closeGetAddOnModal()

    await this.openGetAddOnModal()
  }

  openGetAddOnModalSecondStep = () => {
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      width: 540,
      title: 'Details',
      showCloseButton: false,
      onClose: () => {
        this._paymentError = false
        this._loading = false
        modalStore.removeModal(this._modalId)
      },
      ModalActions: () => <ModalSecondStepActions store={this} />,
      ModalContent: () => <ContentSecondStep store={this} />,
    })
  }

  registerAction = () => {
    uiStore.changeRoute({
      path: `${SettingsPath.compliance.calling.root}`,
    })
    modalStore.removeModal(this._modalId)
  }

  openGetAddOnModal = async () => {
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      width: 540,
      title: 'Details',
      showCloseButton: false,
      ModalActions: () => <ModalFirstStepActions store={this} />,
      ModalContent: () => <ModalContentFirstStep countTitle={this.trustedCallingPriceText} />,
    })
  }
}
